import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { settingsApi } from "@/integrations/mysql/client";

export interface NavConfigItem {
  id: string;
  label: string;
  path: string;
  enabled: boolean;
}

export interface FooterSection {
  id: string;
  title: string;
  items: NavConfigItem[];
}

export type SocialNetwork =
  | 'instagram' | 'facebook' | 'youtube' | 'linkedin'
  | 'x' | 'tiktok' | 'whatsapp' | 'telegram' | 'pinterest' | 'spotify';

export interface SocialLinkItem {
  id: string;
  network: SocialNetwork;
  url: string;
  enabled: boolean;
}

export const DEFAULT_SOCIAL_LINKS: SocialLinkItem[] = [
  { id: 'instagram', network: 'instagram', url: 'https://www.instagram.com/brenda.nutricionista/', enabled: true },
  { id: 'facebook',  network: 'facebook',  url: '#', enabled: true },
  { id: 'youtube',   network: 'youtube',   url: '#', enabled: true },
  { id: 'linkedin',  network: 'linkedin',  url: '#', enabled: true },
  { id: 'x',         network: 'x',         url: '#', enabled: true },
];

/** Items whose visibility also depends on the page-visibility setting */
export const PAGE_VISIBILITY_MAP: Record<string, string> = {
  sobre:           "page_nossa_historia_visible",
  "nossa-historia":"page_nossa_historia_visible",
  servicos:        "page_servicos_visible",
  blog:            "page_blog_visible",
  imprensa:        "page_imprensa_visible",
};

export const DEFAULT_HEADER_ITEMS: NavConfigItem[] = [
  { id: "sobre",       label: "Quem Somos",  path: "/sobre",        enabled: true },
  { id: "servicos",    label: "Serviços",    path: "/servicos",     enabled: true },
  { id: "faq",         label: "FAQ",         path: "/#faq",         enabled: true },
  { id: "agendamento", label: "Agendamento", path: "/avaliacoes",   enabled: true },
  { id: "depoimentos", label: "Depoimentos", path: "/consultorios", enabled: true },
  { id: "contato",     label: "Contato",     path: "/contato",      enabled: true },
  { id: "blog",        label: "Blog",        path: "/blog",         enabled: true },
  { id: "imprensa",    label: "Imprensa",    path: "/imprensa",     enabled: true },
];

export const DEFAULT_FOOTER_SECTIONS: FooterSection[] = [
  {
    id: "empresa", title: "EMPRESA",
    items: [
      { id: "nossa-historia", label: "Quem Somos",      path: "/sobre",    enabled: true },
      { id: "servicos",       label: "Serviços",        path: "/servicos", enabled: true },
      { id: "sitemap",        label: "Mapa do Site",    path: "/sitemap",  enabled: true },
    ],
  },
  {
    id: "pacientes", title: "PACIENTES",
    items: [
      { id: "avaliacao", label: "Avaliação de Caso", path: "/avaliacoes", enabled: true },
    ],
  },
  {
    id: "conteudo", title: "CONTEÚDO",
    items: [
      { id: "blog",     label: "Blog",     path: "/blog",     enabled: true },
      { id: "contato",  label: "Contato",  path: "/contato",  enabled: true },
      { id: "imprensa", label: "Imprensa", path: "/imprensa", enabled: true },
    ],
  },
];

export function useNavConfig() {
  const { data: settings = [] } = useQuery({
    queryKey: ["site-settings"],
    queryFn: settingsApi.list,
    staleTime: 30_000,
  });

  const headerItems = useMemo<NavConfigItem[]>(() => {
    const raw = settings.find(s => s.key === "header_nav_config")?.value;
    if (!raw) return DEFAULT_HEADER_ITEMS;
    try { return JSON.parse(raw) as NavConfigItem[]; } catch { return DEFAULT_HEADER_ITEMS; }
  }, [settings]);

  const footerSections = useMemo<FooterSection[]>(() => {
    const raw = settings.find(s => s.key === "footer_nav_config")?.value;
    if (!raw) return DEFAULT_FOOTER_SECTIONS;
    try { return JSON.parse(raw) as FooterSection[]; } catch { return DEFAULT_FOOTER_SECTIONS; }
  }, [settings]);

  const socialLinks = useMemo<SocialLinkItem[]>(() => {
    const raw = settings.find(s => s.key === "social_links_config")?.value;
    if (!raw) return DEFAULT_SOCIAL_LINKS;
    try { return JSON.parse(raw) as SocialLinkItem[]; } catch { return DEFAULT_SOCIAL_LINKS; }
  }, [settings]);

  const isNavItemVisible = (item: NavConfigItem): boolean => {
    if (!item.enabled) return false;
    const pvKey = PAGE_VISIBILITY_MAP[item.id];
    if (pvKey) {
      const s = settings.find(x => x.key === pvKey);
      return s ? s.value !== "false" : true;
    }
    return true;
  };

  return { headerItems, footerSections, socialLinks, isNavItemVisible };
}
